import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";


import {
  Bell,
  CalendarFold,
  User,
  Users,
  LogOut,
  Inbox,
  NotebookTabs,
  Search,
  Plus,
  ChevronDown,
  ChevronLeft,
  Trash2,
  ChevronRight,
} from "lucide-react";

// Inline-editable field component
const EditableField = ({ value, onSave, placeholder = "-" }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");

  useEffect(() => { setVal(value || ""); }, [value]);

  if (editing) {
    return (
      <div className="flex items-center gap-1">
        <input
          type="text"
          value={val}
          onChange={e => setVal(e.target.value)}
          className="w-24 px-1 py-0.5 text-xs border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
          onKeyDown={e => {
            if (e.key === "Enter") { onSave(val); setEditing(false); }
            if (e.key === "Escape") { setVal(value || ""); setEditing(false); }
          }}
          autoFocus
        />
        <button onClick={() => { onSave(val); setEditing(false); }} className="text-green-600 font-bold text-sm leading-none">✓</button>
        <button onClick={() => { setVal(value || ""); setEditing(false); }} className="text-red-500 font-bold text-sm leading-none">✗</button>
      </div>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:bg-blue-50 px-1 py-0.5 rounded text-xs text-gray-900 inline-block min-w-[40px] border border-transparent hover:border-blue-200"
      title="Click to edit"
    >
      {value || <span className="text-gray-400">{placeholder}</span>}
    </span>
  );
};

const Logbook = () => {
  const [currentTime, setCurrentTime] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedApproval, setSelectedApproval] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const formatToLocalTime = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
  
    const date = new Date(utcTimestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const formatExpectedTime = (utcTimestamp) => {
    if (!utcTimestamp) return "-";
  
    const date = new Date(utcTimestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  useEffect(() => {
    if (location.state?.showSuccessMessage) {
      setShowSuccessMessage(true);
      window.history.replaceState({}, document.title);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000);
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.showSuccessMessage && visits.length > 0) {
      setCurrentPage(1);
    }
  }, [visits, location.state]);

  // Fetch visits data from API — re-runs on every navigation to this page (location.key changes each time)
  useEffect(() => {
    const fetchVisits = async () => {
      try {
        setLoading(true);

        const userData = JSON.parse(localStorage.getItem("user"));
        const userRole = userData?.role || 'Employee';
        const userEmail = userData?.email;

        const response = await fetch("https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/logbook");
        const data = await response.json();
        const visitsArray = Array.isArray(data.logbook) ? data.logbook : [];

        let filteredData = visitsArray;

        if (userRole === "Concierge") {
          const today = new Date().toISOString().split('T')[0];
          filteredData = visitsArray.filter(visit => {
            const visitDate = new Date(visit.timestamp).toISOString().split('T')[0];
            return visitDate === today;
          });
        } else if (userRole === "Employee") {
          filteredData = visitsArray.filter(visit => visit.host === userEmail);
        }

        setVisits(filteredData);

      } catch (error) {
        console.error("Error fetching visits:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVisits();
  }, [location.key]);

  // Update time every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const timeString = now.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
      setCurrentTime(timeString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".status-dropdown")) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
    navigate("/");
  };

  const handleNewVisit = () => {
    navigate("/invite");
  };

  const handleDeleteVisit = async (visitId) => {
    if (!window.confirm("Are you sure you want to delete this visit? This action cannot be undone.")) {
      return;
    }
  
    try {
      const response = await fetch(`https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/logbook/${visitId}`, {
        method: 'DELETE',
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      // Remove the visit from local state
      setVisits(visits.filter(visit => (visit._id || visit.id) !== visitId));
      
      console.log('Visit deleted successfully');
      
    } catch (error) {
      console.error("Error deleting visit:", error);
      alert("Failed to delete visit. Please try again.");
    }
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      await Promise.all(
        [...selectedRows].map(id =>
          fetch(`https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/logbook/${id}`, { method: "DELETE" })
        )
      );
      setVisits(prev => prev.filter(v => !selectedRows.has(v._id || v.id)));
      setSelectedRows(new Set());
      setShowBulkDeleteModal(false);
    } catch (error) {
      console.error("Bulk delete error:", error);
      alert("Failed to delete some visits. Please try again.");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const toggleRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAllRows = () => {
    if (selectedRows.size === currentVisits.length && currentVisits.length > 0) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(currentVisits.map(v => v._id || v.id)));
    }
  };

  const handleStatusChange = async (visitIndex, newStatus) => {
    try {
      const visit = visits[visitIndex];
      const visitId = visit._id || visit.id; 
      
      const response = await fetch(`https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/logbook/${visitId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const result = await response.json();
      
      const updatedVisits = [...visits];
      updatedVisits[visitIndex] = {
        ...updatedVisits[visitIndex],
        status: newStatus,
        checkInTime: result.visit?.checkInTime !== undefined ? result.visit.checkInTime : updatedVisits[visitIndex].checkInTime,
        checkOutTime: result.visit?.checkOutTime !== undefined ? result.visit.checkOutTime : updatedVisits[visitIndex].checkOutTime
      };
      
      setVisits(updatedVisits);
      setOpenDropdown(null);
      
      console.log('Status updated successfully:', result.message);
      
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Failed to update status. Please try again.");
    }
  };

  // Verify vehicle handler
  const handleVerifyVehicle = async (visitId, visitIndex) => {
    const userData = JSON.parse(localStorage.getItem("user"));
    try {
      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/verify-vehicle`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            verified: true,
            remarks: "All good",
            verifiedBy: userData?.email,
          }),
        }
      );
      if (response.ok) {
        const updatedVisits = [...visits];
        updatedVisits[visitIndex] = { ...updatedVisits[visitIndex], vehicleVerified: true, vehicleVerifiedBy: userData?.email };
        setVisits(updatedVisits);
      } else {
        alert("Failed to verify vehicle. Please try again.");
      }
    } catch (error) {
      console.error("Error verifying vehicle:", error);
      alert("Failed to verify vehicle. Please try again.");
    }
  };

  // Update RFID handler
  const handleUpdateRFID = async (visitId, visitIndex, rfidCardNumber) => {
    try {
      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/rfid`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rfidCardNumber }),
        }
      );
      if (response.ok) {
        const updatedVisits = [...visits];
        updatedVisits[visitIndex] = { ...updatedVisits[visitIndex], rfidCardNumber };
        setVisits(updatedVisits);
      } else {
        alert("Failed to update RFID. Please try again.");
      }
    } catch (error) {
      console.error("Error updating RFID:", error);
    }
  };

  // Update vehicle info handler
  const handleUpdateVehicleInfo = async (visitId, visitIndex, field, value) => {
    const visit = visits[visitIndex];
    const updatedLicensePlate = field === "licensePlate" ? value : (visit.licensePlate || "");
    const updatedVehicleInfo = field === "vehicleInfo" ? value : (visit.vehicleInfo || "");
    try {
      const response = await fetch(
        `https://vms-backend-dot-vms-demo-495410.el.r.appspot.com/api/visits/${visitId}/vehicle-info`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            licensePlate: updatedLicensePlate,
            vehicleInfo: updatedVehicleInfo,
          }),
        }
      );
      if (response.ok) {
        const updatedVisits = [...visits];
        updatedVisits[visitIndex] = { ...updatedVisits[visitIndex], [field]: value };
        setVisits(updatedVisits);
      } else {
        alert("Failed to update vehicle info. Please try again.");
      }
    } catch (error) {
      console.error("Error updating vehicle info:", error);
    }
  };

// Status dropdown component
const StatusDropdown = ({ status, visitIndex }) => {
  const isOpen = openDropdown === visitIndex;
  
  const userData = JSON.parse(localStorage.getItem("user"));
  const userRole = userData?.role || 'Employee';
  const userEmail = userData?.email;
  const isAdmin = userRole === "Admin";
  
  // Get the current visit
  const visit = visits[startIndex + visitIndex];
  
  // Allow Admin (all visits) or Employee (only their own visits where they are the host)
  const canChangeStatus = isAdmin || (userRole === "Employee" && visit?.host === userEmail);

  const statusOptions = [
    {
      value: "expected",
      label: "Expected",
      color: "bg-blue-100 text-blue-800 border border-blue-200",
    },
    {
      value: "checked_in",
      label: "Checked-in",
      color: "bg-green-100 text-green-800 border border-green-200",
    },
    {
      value: "checked_out",
      label: "Checked-out",
      color: "bg-gray-100 text-gray-800 border border-gray-200",
    },
  ];

  const getCurrentOption = () => {
    if (status === "expected") {
      return statusOptions[0];
    }
    return (
      statusOptions.find((opt) => opt.value === status) || statusOptions[0]
    );
  };

  const currentOption = getCurrentOption();
  
  // If not authorized to change status, show static badge
  if (!canChangeStatus) {
    return (
      <span className={`px-3 py-1 rounded-md text-xs font-medium ${currentOption.color} w-32 inline-block text-center whitespace-nowrap`}>
        {currentOption.label}
      </span>
    );
  }
  
  // Admin and Employee (for their visits) can click and change status
  return (
    <div className="relative status-dropdown">
      <button
        onClick={() => setOpenDropdown(isOpen ? null : visitIndex)}
        className={`px-3 py-1 rounded-md text-xs font-medium ${currentOption.color} flex items-center justify-center gap-1 cursor-pointer hover:opacity-80 transition-opacity w-32 whitespace-nowrap`}
      >
        <span className="truncate">{currentOption.label}</span>
        <ChevronDown className="w-3 h-3 flex-shrink-0" />
      </button>
  
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 w-32">
          {statusOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleStatusChange(visitIndex, option.value)}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-gray-50 transition-colors whitespace-nowrap ${
                option.value === status ? 'bg-blue-50 text-blue-800' : ''
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

  // Visit-level approval badge for the Approval column
  const VisitApprovalBadge = ({ visit }) => {
    const status = visit?.documentStatus?.toLowerCase();
    const map = {
      "auto-approved": "bg-green-100 text-green-700",
      "approved":      "bg-green-100 text-green-800",
      "rejected":      "bg-red-100 text-red-700",
      "pending":       "bg-yellow-100 text-yellow-800",
    };
    const labels = {
      "auto-approved": "Auto Approved",
      "approved":      "Approved",
      "rejected":      "Rejected",
      "pending":       "Pending",
    };
    if (!status) return <span className="text-xs text-gray-400">—</span>;
    return (
      <span className={`px-2 py-1 rounded-md text-xs font-medium whitespace-nowrap ${map[status] || "bg-gray-100 text-gray-600"}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Agreement badge component
  const AgreementBadge = ({ agreement }) => {
    const getAgreementStyles = (agreement) => {
      switch (agreement?.toLowerCase()) {
        case "signed":
          return "bg-green-100 text-green-800";
        case "pending":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    return (
      <span
        className={`px-3 py-1 rounded-md text-xs font-medium ${getAgreementStyles(
          agreement
        )}`}
      >
        {agreement || "Pending"}
      </span>
    );
  };



  const userData = JSON.parse(localStorage.getItem("user"));
  const userRole = userData?.role || 'Employee';
  const isAdmin = userRole === "Admin";
  const isConcierge = userRole === "Concierge";
  const isEmployee = userRole === "Employee";

  // Filter visits based on search and filters
  const filteredVisits = visits.filter((visit) => {
    const matchesSearch =
      !searchQuery ||
      visit.visitor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.purpose?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      visit.company?.toLowerCase().includes(searchQuery.toLowerCase());
  
    const matchesStatus = !selectedStatus || visit.status === selectedStatus;
    const matchesDate = !selectedDate || 
    (visit.expectedTime && new Date(visit.expectedTime).toISOString().split('T')[0] === selectedDate);
    
    const matchesApproval = !selectedApproval ||
      (selectedApproval === "required" && !!visit.isApprovalRequired) ||
      (selectedApproval === "not_required" && !visit.isApprovalRequired) ||
      (selectedApproval === "doc_required" && !!visit.requiresDocument);
  
    return matchesSearch && matchesStatus && matchesDate && matchesApproval;
  });

  const totalPages = Math.ceil(filteredVisits.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const currentVisits = filteredVisits.slice(startIndex, endIndex);

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar onLogout={handleLogout} />
  
      {/* Main Content */}
      <div className="ml-32 mr-8 py-8">
        {/* Success Message */}
        {showSuccessMessage && (
          <div className="fixed top-8 left-1/2 transform -translate-x-1/2 z-50">
            <div className="bg-white border border-green-200 rounded-lg shadow-lg px-6 py-4 flex items-center gap-3">
              <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <span className="text-green-800 font-medium">
                Invite created successfully!
              </span>
              <button
                onClick={() => setShowSuccessMessage(false)}
                className="text-green-600 hover:text-green-800"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-semibold text-gray-900">Logbook</h1>
          <div className="flex items-center gap-3">
            {selectedRows.size > 0 && (
              <button
                onClick={handleBulkDelete}
                className="bg-red-700 text-white px-5 py-2 rounded-lg font-medium hover:bg-red-800 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete Selected ({selectedRows.size})
              </button>
            )}
            <button
              onClick={handleNewVisit}
              className="bg-blue-800 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-900 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              New Visit
            </button>
          </div>
        </div>
  
        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by visitor name, company, purpose..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
          />
        </div>
  
      {/* Filters */}
{/* Filters */}
<div className="flex gap-4 mb-6">
  {isAdmin && (
    <div className="flex flex-col gap-1">
      <label className="text-sm text-gray-600 font-medium">
        Filter date (by visit)
      </label>
      <input
        type="date"
        placeholder="Date"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
      />
    </div>
  )}
  
  <div className="flex flex-col gap-1">
    <label className="text-sm text-gray-600 font-medium">
      Status
    </label>
    <div className="relative">
      <select
        value={selectedStatus}
        onChange={(e) => setSelectedStatus(e.target.value)}
        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none pr-10"
      >
        <option value="">All Status</option>
        <option value="expected">Expected</option>
        <option value="checked_in">Checked In</option>
        <option value="checked_out">Checked Out</option>
      </select>
      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
    </div>
  </div>

  <div className="flex flex-col gap-1">
    <label className="text-sm text-gray-600 font-medium">
      Approval Type
    </label>
    <div className="relative">
      <select
        value={selectedApproval}
        onChange={(e) => setSelectedApproval(e.target.value)}
        className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white appearance-none pr-10"
      >
        <option value="">All Visits</option>
        <option value="required">Approval Required</option>
        <option value="not_required">No Approval Required</option>
      </select>
      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none" />
    </div>
  </div>
  
  {(isConcierge || isEmployee) && (
    <div className="flex items-center px-3 py-2 bg-gray-100 text-blue-800 rounded-lg text-sm">
      {isConcierge && "Today's visits"}
      {isEmployee && "Your Visitor Invites"}
    </div>
  )}
</div>
  
       {/* Table */}
       <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full min-w-max">
    <thead className="bg-gray-50 border-b border-gray-200">
      <tr>
        <th className="px-3 py-4 text-center text-xs font-medium text-gray-600 w-10">
          <input
            type="checkbox"
            className="w-4 h-4 rounded border-gray-300 text-blue-700 cursor-pointer"
            checked={selectedRows.size === currentVisits.length && currentVisits.length > 0}
            onChange={toggleAllRows}
          />
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Status
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Expected Time
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Check-in
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Check-out
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Visitor
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Company
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          License Plate
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          RFID Card
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Vehicle Info
        </th>
        {isConcierge && (
          <th className="px-3 py-4 text-center text-xs font-medium text-gray-600 whitespace-nowrap">
            Verify Vehicle
          </th>
        )}
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Host
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Approval
        </th>
        <th className="px-3 py-4 text-left text-xs font-medium text-gray-600 whitespace-nowrap">
          Agreement
        </th>
        <th className="px-3 py-4 text-center text-xs font-medium text-gray-600 whitespace-nowrap">
          Review
        </th>
      </tr>
    </thead>
    <tbody className="divide-y divide-gray-200">
      {loading ? (
        <tr>
          <td
            colSpan={isConcierge ? 15 : 14}
            className="px-6 py-8 text-center text-gray-500 text-xs"
          >
            Loading...
          </td>
        </tr>
      ) : currentVisits.length === 0 ? (
        <tr>
          <td
            colSpan={isConcierge ? 15 : 14}
            className="px-6 py-8 text-center text-gray-500 text-xs"
          >
            No visits found
          </td>
        </tr>
      ) : (
        currentVisits.map((visit, index) => {
          const visitId = visit._id || visit.id;
          const isSelected = selectedRows.has(visitId);
          return (
            <tr
              key={visitId || (visit.timestamp + index)}
              className={`hover:bg-gray-50 transition-colors ${isSelected ? "bg-blue-50" : ""}`}
            >
              <td className="px-3 py-4 text-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-blue-700 cursor-pointer"
                  checked={isSelected}
                  onChange={() => toggleRow(visitId)}
                />
              </td>
              <td className="px-3 py-4 overflow-visible">
                <StatusDropdown
                  status={visit.status}
                  visitIndex={startIndex + index}
                />
              </td>
              <td className="px-3 py-4 text-xs text-gray-900 whitespace-nowrap">
                {formatExpectedTime(visit.expectedTime)}
              </td>
              <td className="px-3 py-4 text-xs text-gray-900 whitespace-nowrap">
                {formatToLocalTime(visit.checkInTime)}
              </td>
              <td className="px-3 py-4 text-xs text-gray-900 whitespace-nowrap">
                {formatToLocalTime(visit.checkOutTime)}
              </td>
              <td className="px-3 py-4 text-xs text-gray-900 whitespace-nowrap">
                {visit.visitor || "-"}
              </td>
              <td className="px-3 py-4 text-xs text-gray-900 whitespace-nowrap">
                {visit.company || "-"}
              </td>
              <td className="px-3 py-4 text-xs text-gray-900">
                <EditableField
                  value={visit.licensePlate}
                  placeholder="—"
                  onSave={(val) =>
                    handleUpdateVehicleInfo(visitId, startIndex + index, "licensePlate", val)
                  }
                />
              </td>
              <td className="px-3 py-4 text-xs text-gray-900">
                <EditableField
                  value={visit.rfidCardNumber}
                  placeholder="—"
                  onSave={(val) =>
                    handleUpdateRFID(visitId, startIndex + index, val)
                  }
                />
              </td>
              <td className="px-3 py-4 text-xs text-gray-900">
                <EditableField
                  value={visit.vehicleInfo}
                  placeholder="—"
                  onSave={(val) =>
                    handleUpdateVehicleInfo(visitId, startIndex + index, "vehicleInfo", val)
                  }
                />
              </td>
              {isConcierge && (
                <td className="px-3 py-4 text-center">
                  {visit.vehicleVerified ? (
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 border border-green-300 rounded-md font-medium whitespace-nowrap">
                      ✓ Verified
                    </span>
                  ) : !visit.licensePlate || visit.licensePlate === '-' ? (
                    <span className="text-xs text-gray-400 font-medium">NA</span>
                  ) : (
                    <button
                      onClick={() => handleVerifyVehicle(visitId, startIndex + index)}
                      className="px-2 py-1 text-xs bg-blue-700 text-white rounded-md font-medium hover:bg-blue-800 transition-colors whitespace-nowrap"
                    >
                      Verify
                    </button>
                  )}
                </td>
              )}
              <td className="px-3 py-4 text-xs text-gray-900 relative group whitespace-nowrap">
                <span className="cursor-pointer">
                  {visit.hostName || visit.hostname || "-"}
                </span>
                {visit.host && (
                  <div className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-500 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                    {visit.host}
                    <div className="absolute top-full left-1/3 transform -translate-x-1/2 border-4 border-transparent border-t-gray-500"></div>
                  </div>
                )}
              </td>
              <td className="px-3 py-4">
                <VisitApprovalBadge visit={visit} />
              </td>
              <td className="px-3 py-4">
                <AgreementBadge agreement={visit.agreement} />
              </td>
              <td className="px-3 py-4 text-center">
                <button
                  onClick={() => navigate(`/visit/${visitId}`)}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 hover:bg-blue-50 transition-colors"
                  title="View details"
                >
                  <svg
                    className="w-5 h-5 text-blue-800"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </td>
            </tr>
          );
        })
      )}
    </tbody>
  </table>
</div>

        {/* Pagination */}
        <div className="flex items-center justify-between mt-6">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Showing {startIndex + 1}-
              {Math.min(endIndex, filteredVisits.length)} of{" "}
              {filteredVisits.length}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 border rounded ${
                      currentPage === pageNum
                        ? "bg-blue-800 text-white border-blue-800"
                        : "border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() =>
                setCurrentPage(Math.min(totalPages, currentPage + 1))
              }
              disabled={currentPage === totalPages}
              className="p-2 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-gray-900">Delete {selectedRows.size} visit{selectedRows.size > 1 ? "s" : ""}?</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              This will permanently remove the selected {selectedRows.size > 1 ? `${selectedRows.size} visits` : "visit"} from the logbook. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                {isBulkDeleting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete {selectedRows.size > 1 ? `${selectedRows.size} visits` : "visit"}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logbook;